import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import buildImageUrl from '../../utils/imageUrl';
import './ItemsManager.css';

const ItemsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock_quantity: '1',
    category: '',
    is_available: true,
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/items');
      setItems(response.data);
    } catch (err) {
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/admin/items/${editingItem.id}`, formData);
      } else {
        await api.post('/admin/items', formData);
      }
      resetForm();
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await api.delete(`/admin/items/${id}`);
      fetchItems();
    } catch (err) {
      alert('Failed to delete item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price.toString(),
      stock_quantity: item.stock_quantity.toString(),
      category: item.category || '',
      is_available: item.is_available,
    });
    setShowForm(true);
  };

  const handleImageUpload = async (itemId, file) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      await api.post(`/admin/items/${itemId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchItems();
    } catch (err) {
      alert('Failed to upload image');
    }
  };

  const handleImageDelete = async (itemId, imageId) => {
    if (!window.confirm('Delete this image?')) return;
    
    try {
      await api.delete(`/admin/items/${itemId}/images/${imageId}`);
      fetchItems();
    } catch (err) {
      alert('Failed to delete image');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      stock_quantity: '1',
      category: '',
      is_available: true,
    });
    setEditingItem(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading">Loading items...</div>;
  }

  return (
    <div className="items-manager">
      <div className="manager-header">
        <h1>Manage Items</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add Item'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="item-form card" onSubmit={handleSubmit}>
          <h2>{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Price *</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Stock Quantity</label>
              <input
                type="number"
                value={formData.stock_quantity}
                onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_available}
                onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
              />
              <span>Available for purchase</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {editingItem ? 'Update Item' : 'Create Item'}
            </button>
            <button type="button" className="btn btn-outline" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="items-list">
        {items.map((item) => (
          <div key={item.id} className="item-card-admin card">
            <div className="item-images-admin">
              {item.images.map((img) => (
                <div key={img.id} className="item-image-wrapper">
                  <img
                    src={buildImageUrl(img.image_url)}
                    alt={item.name}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                  <button
                    className="delete-image-btn"
                    onClick={() => handleImageDelete(item.id, img.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <label className="upload-image-btn">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(item.id, e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <span>+ Add Image</span>
              </label>
            </div>

            <div className="item-info-admin">
              <h3>{item.name}</h3>
              {item.category && <p className="category">{item.category}</p>}
              <p className="price">${item.price.toFixed(2)}</p>
              {item.description && <p className="description">{item.description}</p>}
              <div className="item-meta">
                <span>Stock: {item.stock_quantity}</span>
                <span className={item.is_available ? 'available' : 'unavailable'}>
                  {item.is_available ? 'Available' : 'Unavailable'}
                </span>
              </div>
            </div>

            <div className="item-actions">
              <button className="btn btn-outline" onClick={() => handleEdit(item)}>
                Edit
              </button>
              <button className="btn btn-outline delete-btn" onClick={() => handleDelete(item.id)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ItemsManager;
